// packages import
import { FC, useContext, useState } from 'react'
import { TableBody, TableCell, TableHead, TableRow, Typography, Tooltip, IconButton } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import { Link } from 'react-router-dom'
import dayjs from 'dayjs'

// component imports
import DeleteInterestEntriesModal from './DeleteInterestEntriesModal'
import { CustomPagination } from '../../common/CustomPagination'
import { StickyTable } from '../../common/StickyTable'
import { TableSkeleton } from '../../skeletons/TableSkeleton'

// others
import { InterestEntriesContext } from '../../../context/InterestEntriesContext'
import { formatCurrency } from '../../../utils'
import palette from '../../../theme/palette'
import { BillStatus } from '../../../../generated'
import { formatStatus } from '../../../utils/billing'

export const NON_DELETABLE_STATUSES: BillStatus[] = [
  BillStatus.PartiallyPaid,
  BillStatus.Paid,
  BillStatus.PrePaid,
  BillStatus.WrittenOff
]

interface DeleteTarget {
  billId: number
  clientId: string
  clientName: string
  defaultFromDate: string
  writeOffInterest: boolean
}

export const InterestEntriesListing: FC = () => {
  const { interestEntries, loading, refetch } = useContext(InterestEntriesContext)
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null)

  return (
    <>
      <StickyTable pagination={<CustomPagination loading={loading || false} setParams={true} />} height="400px">
        <TableHead>
          <TableRow>
            <TableCell width="11%">Date</TableCell>
            <TableCell width="8%">Bill #</TableCell>
            <TableCell width="10%">Matter #</TableCell>
            <TableCell width="22%">Matter</TableCell>
            <TableCell width="17%">Client</TableCell>
            <TableCell width="8%">Rate</TableCell>
            <TableCell width="10%">Accrued</TableCell>
            <TableCell width="10%">Total</TableCell>
            <TableCell width="4%"></TableCell>
          </TableRow>
        </TableHead>

        {loading ? (
          <TableSkeleton noOfRows={7} noOfCols={9} />
        ) : (
          <TableBody>
            {interestEntries?.map(entry => {
              const { id, date, rate, accruedInterest, cumulativeInterest, bill, client } = entry || {}
              const caseData = bill?.case
              const clientName =
                client?.businessName ||
                `${client?.firstName || ''} ${client?.lastName || ''}`.replace(/\s+/g, ' ').trim()
              const canDelete = bill?.status && !NON_DELETABLE_STATUSES.includes(bill.status as BillStatus)

              return (
                <TableRow key={id} hover>
                  <TableCell>{date ? dayjs(date).format('MM/DD/YYYY') : ''}</TableCell>

                  <TableCell>
                    {bill?.id ? (
                      <Link
                        to={`/billing/bill-detail/${bill.id}`}
                        style={{ color: palette.primary.main, textDecoration: 'none' }}
                      >
                        {bill.id}
                      </Link>
                    ) : (
                      ''
                    )}
                  </TableCell>

                  <TableCell>
                    {caseData?.caseNumber ? (
                      <Link
                        to={`/matters/matter-detail/${caseData.id}`}
                        style={{ color: palette.primary.main, textDecoration: 'none' }}
                      >
                        #{caseData.caseNumber}
                      </Link>
                    ) : (
                      ''
                    )}
                  </TableCell>

                  <TableCell>
                    <Tooltip title={caseData?.title || ''} arrow placement="top">
                      <Typography noWrap sx={{ textOverflow: 'ellipsis', overflow: 'hidden', maxWidth: '300px' }}>
                        {caseData?.title || ''}
                      </Typography>
                    </Tooltip>
                  </TableCell>

                  <TableCell>
                    <Typography noWrap sx={{ textOverflow: 'ellipsis', overflow: 'hidden', maxWidth: '220px' }}>
                      {client?.clientNumber ? `#${client.clientNumber}` : null} {clientName}
                    </Typography>
                  </TableCell>

                  <TableCell>{rate != null ? `${(Number(rate) * 100).toFixed(2)}%` : ''}</TableCell>
                  <TableCell>
                    {(accruedInterest || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                  </TableCell>
                  <TableCell>
                    {(cumulativeInterest || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                  </TableCell>

                  <TableCell align="center" padding="none">
                    <Tooltip
                      title={
                        canDelete
                          ? 'Delete entries from this date onwards'
                          : `Cannot delete: Bill is ${formatStatus(bill?.status)}`
                      }
                      arrow
                    >
                      <span>
                        <IconButton
                          size="small"
                          disabled={!canDelete}
                          onClick={() =>
                            canDelete &&
                            id &&
                            bill?.id &&
                            client?.id &&
                            setDeleteTarget({
                              billId: bill.id,
                              clientId: client.id,
                              clientName,
                              defaultFromDate: date ? String(date) : new Date().toISOString(),
                              writeOffInterest: bill.case?.billing?.writeOffInterest ?? false
                            })
                          }
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        )}
      </StickyTable>

      <DeleteInterestEntriesModal
        open={!!deleteTarget}
        billId={deleteTarget?.billId}
        clientId={deleteTarget?.clientId}
        clientName={deleteTarget?.clientName}
        defaultFromDate={deleteTarget?.defaultFromDate}
        defaultWriteOffInterest={deleteTarget?.writeOffInterest ?? false}
        onClose={() => setDeleteTarget(null)}
        onSuccess={() => refetch?.()}
      />
    </>
  )
}
